import type { I18nKey, LocalisedString } from './types'

export interface TeamMember {
  name: string
  role: I18nKey
  /** Portrait path. Optional — members without a photo render an initial-based placeholder. */
  photo?: string
}

export interface Salon {
  id: string
  name: string
  address: string
  postalCode: string
  city: string
  phone: string
  bookingUrl: string
  hours: {
    weekdays: string
    friday?: string
    saturday: string
    sunday: string
  }
  coordinates: { lat: number; lng: number }
  image: string
  tagline: I18nKey
  description: I18nKey
  team: TeamMember[]
  /** How to get there. Transit beats neighbourhood for DK-local intent. */
  transit: I18nKey
  /**
   * Parking line. Free / paid / nearest lot.
   *
   * Intentionally NOT migrated to the salons JSON namespace: parking is the one
   * admin-overridable LocalisedString — resolveSalon (src/lib/salons.ts) merges
   * the admin's Partial<LocalisedString> from content.json over this object,
   * and scripts/check-content-defaults.mjs regex-parses the literal en/da
   * values out of this file to guard admin-snapshot drift.
   */
  parking: LocalisedString
  /** Step-free, ground floor, lift availability. */
  accessibility: I18nKey
}

export const salons: Salon[] = [
  {
    id: 'vanlose',
    name: 'HELIN Hair Vanløse',
    address: 'Vanløse Torv 1, st. 121',
    postalCode: '2720',
    city: 'Vanløse',
    phone: '+45 60 20 01 01',
    bookingUrl: 'https://booksalon.dk/helin-hair-vanlose',
    hours: { weekdays: '10:00–19:00', saturday: '10:00–17:00', sunday: 'closed' },
    coordinates: { lat: 55.6833, lng: 12.4833 },
    image: '/images/saloon-images/vanlose/hero.webp',
    tagline: 'salons:branches.vanlose.tagline',
    description: 'salons:branches.vanlose.description',
    team: [
      { name: 'Jessica', role: 'salons:branches.vanlose.team.0.role' },
      { name: 'Marina', role: 'salons:branches.vanlose.team.1.role' },
    ],
    transit: 'salons:branches.vanlose.transit',
    parking: {
      en: 'Paid street parking on Vanløse Torv. Free at Vanløse Centret after 18:00.',
      da: 'Betalingsparkering på Vanløse Torv. Gratis i Vanløse Centret efter 18:00.',
      de: 'Gebuhrenpflichtige Parkplatze am Vanløse Torv. Kostenlos im Vanløse Centret ab 18:00.',
      sv: 'Avgiftsbelagd gatuparkering vid Vanløse Torv. Gratis i Vanløse Centret efter 18:00.',
      nb: 'Avgiftsbelagt gateparkering ved Vanløse Torv. Gratis i Vanløse Centret etter 18:00.',
      fr: 'Stationnement payant dans la rue sur Vanløse Torv. Gratuit au Vanløse Centret apres 18h00.',
      es: 'Aparcamiento de pago en la calle en Vanløse Torv. Gratis en Vanløse Centret despues de las 18:00.',
      pt: 'Estacionamento pago na rua na Vanløse Torv. Gratis no Vanløse Centret apos as 18:00.',
      nl: 'Betaald straatparkeren op Vanløse Torv. Gratis bij Vanløse Centret na 18:00.',
      pl: 'Platny parking przy ulicy na Vanløse Torv. Bezplatny w Vanløse Centret po 18:00.',
      it: 'Parcheggio su strada a pagamento in Vanløse Torv. Gratuito al Vanløse Centret dopo le 18:00.',
    },
    accessibility: 'salons:branches.vanlose.accessibility',
  },
  {
    id: 'vaerlose',
    name: 'HELIN Hair Værløse',
    address: 'Bymidten 40',
    postalCode: '3500',
    city: 'Værløse',
    phone: '+45 26 25 92 92',
    bookingUrl: 'https://booksalon.dk/helin-hair-vaerlose/book/',
    hours: { weekdays: '10:00–18:00', saturday: '09:00–15:00', sunday: 'closed' },
    coordinates: { lat: 55.7833, lng: 12.3667 },
    image: '/images/saloon-images/vaerlose/hero.webp',
    tagline: 'salons:branches.vaerlose.tagline',
    description: 'salons:branches.vaerlose.description',
    team: [
      { name: 'Sipan', role: 'salons:branches.vaerlose.team.0.role', photo: '/images/saloon-images/vaerlose/sipan.avif' },
      { name: 'Sarah', role: 'salons:branches.vaerlose.team.1.role', photo: '/images/saloon-images/vaerlose/sarah.avif' },
      { name: 'Svetlana', role: 'salons:branches.vaerlose.team.2.role' },
      { name: 'Maria', role: 'salons:branches.vaerlose.team.3.role', photo: '/images/saloon-images/vaerlose/maria.avif' },
      { name: 'Melina', role: 'salons:branches.vaerlose.team.4.role' },
    ],
    transit: 'salons:branches.vaerlose.transit',
    parking: {
      en: 'Free parking at Bymidten, 2 min walk to the salon.',
      da: 'Gratis parkering ved Bymidten, 2 min gang til salonen.',
      de: 'Kostenlose Parkplatze am Bymidten, 2 Minuten zu Fuss zum Salon.',
      sv: 'Gratis parkering vid Bymidten, 2 min promenad till salongen.',
      nb: 'Gratis parkering ved Bymidten, 2 min gange til salongen.',
      fr: 'Stationnement gratuit a Bymidten, a 2 min a pied du salon.',
      es: 'Aparcamiento gratuito en Bymidten, a 2 min a pie del salon.',
      pt: 'Estacionamento gratis em Bymidten, a 2 min a pe do salao.',
      nl: 'Gratis parkeren bij Bymidten, 2 min lopen naar de salon.',
      pl: 'Bezplatny parking przy Bymidten, 2 min pieszo do salonu.',
      it: 'Parcheggio gratuito a Bymidten, a 2 min a piedi dal salone.',
    },
    accessibility: 'salons:branches.vaerlose.accessibility',
  },
  {
    id: 'horsholm',
    name: 'HELIN Hair Hørsholm',
    address: 'Hørsholm Midtpunkt 43',
    postalCode: '2970',
    city: 'Hørsholm',
    phone: '+45 21 99 06 50',
    bookingUrl: 'https://booksalon.dk/helin-hair/',
    hours: { weekdays: '10:00–18:00', saturday: '10:00–16:00', sunday: '11:00–16:00' },
    coordinates: { lat: 55.8833, lng: 12.5 },
    image: '/images/saloon-images/horsholm/hero.webp',
    tagline: 'salons:branches.horsholm.tagline',
    description: 'salons:branches.horsholm.description',
    team: [
      { name: 'Massimo', role: 'salons:branches.horsholm.team.0.role', photo: '/images/saloon-images/horsholm/massimo.avif' },
      { name: 'Naja', role: 'salons:branches.horsholm.team.1.role', photo: '/images/saloon-images/horsholm/naja.avif' },
      { name: 'Lile', role: 'salons:branches.horsholm.team.2.role', photo: '/images/saloon-images/horsholm/lile.avif' },
      { name: 'Danyelle', role: 'salons:branches.horsholm.team.3.role', photo: '/images/saloon-images/horsholm/danyelle.avif' },
      { name: 'Edgar', role: 'salons:branches.horsholm.team.4.role', photo: '/images/saloon-images/horsholm/edgar.avif' },
    ],
    transit: 'salons:branches.horsholm.transit',
    parking: {
      en: 'Free parking at Hørsholm Midtpunkt, 2 hours covered.',
      da: 'Gratis parkering ved Hørsholm Midtpunkt, 2 timer.',
      de: 'Kostenlose Parkplatze am Hørsholm Midtpunkt, 2 Stunden frei.',
      sv: 'Gratis parkering vid Hørsholm Midtpunkt, 2 timmar.',
      nb: 'Gratis parkering ved Hørsholm Midtpunkt, 2 timer.',
      fr: 'Stationnement gratuit a Hørsholm Midtpunkt, 2 heures incluses.',
      es: 'Aparcamiento gratuito en Hørsholm Midtpunkt, 2 horas incluidas.',
      pt: 'Estacionamento gratis no Hørsholm Midtpunkt, 2 horas incluidas.',
      nl: 'Gratis parkeren bij Hørsholm Midtpunkt, 2 uur gratis.',
      pl: 'Bezplatny parking przy Hørsholm Midtpunkt, 2 godziny gratis.',
      it: 'Parcheggio gratuito all\'Hørsholm Midtpunkt, 2 ore incluse.',
    },
    accessibility: 'salons:branches.horsholm.accessibility',
  },
  {
    id: 'farum',
    name: 'HELIN Hair Farum',
    address: 'Farum Bytorv 53',
    postalCode: '3520',
    city: 'Farum',
    phone: '+45 21 99 02 27',
    bookingUrl: 'https://booksalon.dk/helin-hair-farum/',
    hours: { weekdays: '09:30–18:00', friday: '09:30–19:00', saturday: '09:30–16:00', sunday: 'closed' },
    coordinates: { lat: 55.8097, lng: 12.3697 },
    image: '/images/saloon-images/farum/hero.webp',
    tagline: 'salons:branches.farum.tagline',
    description: 'salons:branches.farum.description',
    team: [
      { name: 'Sevin', role: 'salons:branches.farum.team.0.role', photo: '/images/saloon-images/farum/sevin.avif' },
      { name: 'Tanya', role: 'salons:branches.farum.team.1.role', photo: '/images/saloon-images/farum/tanya.avif' },
      { name: 'Marcelo', role: 'salons:branches.farum.team.2.role' },
    ],
    transit: 'salons:branches.farum.transit',
    parking: {
      en: 'Free parking at Farum Bytorv, lift to street level.',
      da: 'Gratis parkering ved Farum Bytorv, elevator til gadeplan.',
      de: 'Kostenlose Parkplatze am Farum Bytorv, Aufzug zur Strassenebene.',
      sv: 'Gratis parkering vid Farum Bytorv, hiss till gatuplan.',
      nb: 'Gratis parkering ved Farum Bytorv, heis til gateplan.',
      fr: 'Stationnement gratuit a Farum Bytorv, ascenseur jusqu\'au niveau de la rue.',
      es: 'Aparcamiento gratuito en Farum Bytorv, ascensor hasta el nivel de la calle.',
      pt: 'Estacionamento gratis na Farum Bytorv, elevador ate o nivel da rua.',
      nl: 'Gratis parkeren bij Farum Bytorv, lift naar straatniveau.',
      pl: 'Bezplatny parking przy Farum Bytorv, winda na poziom ulicy.',
      it: 'Parcheggio gratuito a Farum Bytorv, ascensore fino al livello strada.',
    },
    accessibility: 'salons:branches.farum.accessibility',
  },
  {
    id: 'allerod',
    name: 'HELIN Hair Allerød',
    address: 'M.D. Madsensvej 14B',
    postalCode: '3450',
    city: 'Allerød',
    phone: '+45 29 17 16 61',
    bookingUrl: 'https://book.timma.dk/helinhairallerodaps',
    hours: { weekdays: '10:00–18:00', saturday: '10:00–16:00', sunday: 'closed' },
    coordinates: { lat: 55.8667, lng: 12.3333 },
    image: '/images/saloon-images/allerod/hero.webp',
    tagline: 'salons:branches.allerod.tagline',
    description: 'salons:branches.allerod.description',
    team: [
      { name: 'Kawa', role: 'salons:branches.allerod.team.0.role', photo: '/images/saloon-images/allerod/kawa.avif' },
      { name: 'Narges', role: 'salons:branches.allerod.team.1.role', photo: '/images/saloon-images/allerod/narges.avif' },
      { name: 'Warshin', role: 'salons:branches.allerod.team.2.role', photo: '/images/saloon-images/allerod/warshin.avif' },
      { name: 'Adam', role: 'salons:branches.allerod.team.3.role', photo: '/images/saloon-images/allerod/adam.avif' },
    ],
    transit: 'salons:branches.allerod.transit',
    parking: {
      en: 'Free street parking on M.D. Madsensvej.',
      da: 'Gratis gadeparkering på M.D. Madsensvej.',
      de: 'Kostenlose Strassenparkplatze an der M.D. Madsensvej.',
      sv: 'Gratis gatuparkering pa M.D. Madsensvej.',
      nb: 'Gratis gateparkering pa M.D. Madsensvej.',
      fr: 'Stationnement gratuit dans la rue sur M.D. Madsensvej.',
      es: 'Aparcamiento gratuito en la calle M.D. Madsensvej.',
      pt: 'Estacionamento gratis na rua M.D. Madsensvej.',
      nl: 'Gratis straatparkeren op M.D. Madsensvej.',
      pl: 'Bezplatny parking przy ulicy M.D. Madsensvej.',
      it: 'Parcheggio gratuito su strada in M.D. Madsensvej.',
    },
    accessibility: 'salons:branches.allerod.accessibility',
  },
]

export function getSalonById(id: string): Salon | undefined {
  return salons.find((s) => s.id === id)
}
